import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SharesProvider } from './contexts/SharesContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { queryClient } from './lib/queryClient';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
import Docs from './pages/Docs';
import Dashboard from './pages/Dashboard';
import Meetings from './pages/Meetings';
import MeetingEditor from './pages/MeetingEditor';
import MeetingPreview from './pages/MeetingPreview';
import TopicHistoryPreview from './pages/TopicHistoryPreview';
import AllMeetingsPreview from './pages/AllMeetingsPreview';
import MapaAtas from './pages/MapaAtas';
import InviteAccept from './pages/InviteAccept';
import EquipePage from './pages/Equipe';
import LandingPage from './pages/LandingPage';
import PrivateRoute from './components/PrivateRoute';
import { BlockedAccountBanner } from './components/BlockedAccountBanner';
import { useEffect, lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Toaster } from 'sonner';

const AdminUsers = lazy(() => import('./pages/Admin'));
const AdminUserDetails = lazy(() => import('./pages/Admin/UserDetails'));

function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-background rounded-2xl shadow-2xl p-12 text-center">
          <img src="/stant-di.png" alt="Stant" className="w-40 h-auto mx-auto mb-6 animate-pulse" />
          <div className="text-lg font-medium text-muted-foreground">Carregando...</div>
        </div>
      </div>
    );
  }

  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function AppContent() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();


  useEffect(() => {
    const handleLogout = async (e: Event) => {
      const reason = (e as CustomEvent)?.detail?.reason;
      await logout();
      navigate('/login', { replace: true, state: reason === 'account_inactive' ? { accountInactive: true } : undefined });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [navigate, logout]);

  return (
    <>
      <BlockedAccountBanner isBlocked={!!(user as any)?.isBlocked} />

      <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/lp" element={<LandingPage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset-password/confirm" element={<ResetPasswordConfirm />} />

      <Route path="/invite/accept" element={<InviteAccept />} />
      <Route path="/docs/*" element={<Docs />} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/equipe" element={<PrivateRoute><EquipePage /></PrivateRoute>} />
      <Route path="/projects/:id/meetings" element={<PrivateRoute><Meetings /></PrivateRoute>} />
      <Route path="/projects/:id/meetings/:meetingId" element={<PrivateRoute><MeetingEditor /></PrivateRoute>} />
      <Route path="/projects/:id/meetings/:meetingId/preview" element={<PrivateRoute><MeetingPreview /></PrivateRoute>} />
      <Route path="/projects/:id/topics/:topicId/history" element={<PrivateRoute><TopicHistoryPreview /></PrivateRoute>} />
      <Route path="/projects/:id/all-meetings-preview" element={<PrivateRoute><AllMeetingsPreview /></PrivateRoute>} />
      <Route path="/projects/:id/mapa-atas" element={<PrivateRoute><MapaAtas /></PrivateRoute>} />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Suspense fallback={
              <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            }>
              <AdminUsers />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <PrivateRoute>
            <Suspense fallback={
              <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            }>
              <AdminUserDetails />
            </Suspense>
          </PrivateRoute>
        }
      />
    </Routes>
    </>
  );
}

const ROUTES_WITHOUT_FOOTER = ['/login', '/register', '/reset-password', '/reset-password/confirm', '/lp', '/landing'];

function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isLanding = pathname === '/lp' || pathname === '/landing';
  return <div className={isLanding ? '' : 'pb-12'}>{children}</div>;
}

function GlobalFooter() {
  const { pathname } = useLocation();
  if (ROUTES_WITHOUT_FOOTER.includes(pathname)) return null;
  return (
    <footer
      className="fixed bottom-0 left-0 w-full border-t border-border bg-background text-center text-xs text-muted-foreground py-3 z-50 shadow-sm no-print print:hidden"
      style={{boxSizing: 'border-box'}}
    >
      Atas Digitais Stant — Copyright © 2026 | Todos os Direitos Reservados
    </footer>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SharesProvider>
            <BrowserRouter>
              <AppShell>
                <AppContent />
              </AppShell>
              <Toaster position="bottom-right" richColors closeButton />
              <GlobalFooter />
            </BrowserRouter>
          </SharesProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
